// Section 22 Jobs tab and Section 8: job columns with counts and eligibility, per-job projected
// output for the next four seasons (with a no-changes baseline so the dip from a reassignment is
// visible: the bullwhip guard-rail), a "needs" line per resource, vacancies, the dip preview for
// the selected person, and the auto-fill proposal as a diff.
import {
  catalog,
  emptyDecision,
  evaluateTarget,
  validateDecision,
  type BuildingTypeId,
  type Config,
  type Decision,
  type GameState,
  type JobId,
  type PersonId,
  type Projection,
  type RationResource,
  type Resource,
  type Season,
  type ServiceKind,
} from '../sim/api';
import { memo } from './memo';
import { peopleRows, type PersonRow } from './people';

/**
 * Each job's primary output (Section 8). UI knowledge for labelling the sparkline; a job not
 * listed shows no series. The series is the job's own projected output
 * (`ProjectedSeason.outputs[jobId]`, M6), so jobs sharing a resource are told apart.
 */
const PRIMARY: Partial<Record<JobId, Resource | ServiceKind>> = {
  farmer: 'food',
  forager: 'food',
  hunter: 'food',
  'water-carrier': 'water',
  woodcutter: 'wood',
  quarrier: 'stone',
  toolmaker: 'tools',
  healer: 'medicine',
  weaver: 'cloth',
  builder: 'buildPoints',
  teacher: 'teachingSeats',
  caretaker: 'careSlots',
  guard: 'safetyPoints',
};

export interface DipPreview {
  /** Productivity multiplier in the current job (output / the job's base yield), or null if resting. */
  now: number | null;
  /** Productivity multiplier in this column's job. */
  moved: number;
  skillNow: number;
  skillMoved: number;
  /** A farmer leaving a planted plot loses its harvest (Section 8). */
  losesPlot: boolean;
}

export interface JobColumnVM {
  id: JobId;
  label: string;
  heavy: boolean;
  requiresBuilding: BuildingTypeId | null;
  requiresLabel: string | null;
  people: PersonRow[];
  count: number;
  /** People who may do this job this season. */
  eligible: number;
  /** Posts left vacant last season (deaths, departures, age, inability). */
  vacancies: { personId: PersonId; name: string; reason: string }[];
  output: Resource | ServiceKind | null;
  /** This job's projected output over the horizon, under the draft. */
  series: number[];
  /** The same under no changes this season. */
  baseline: number[];
  /** The selected person may take this job (null if nobody is selected). */
  selectedEligible: boolean | null;
  selectedProblem: string | null;
  dip: DipPreview | null;
}

export interface NeedLine {
  resource: RationResource;
  /** Total demand next season (Section 6), from explain(demand). */
  needed: number;
  /** Projected production next season of the stock that meets it. */
  projected: number;
  onHand: number;
  stock: Resource;
}

export interface JobsVM {
  columns: JobColumnVM[];
  /** The selected person is a farmer tending a planted plot: moving them loses its harvest. */
  selectedLosesPlot: boolean;
  needs: NeedLine[];
  horizon: { season: Season; year: number }[];
  pending: boolean;
  selected: PersonRow | null;
}

/** Who may do each job this season: one validation per job with everyone assigned to it. */
export const eligibilityOf = memo(
  (game: GameState, cfg?: Config): Map<JobId, Map<PersonId, string>> => {
    const out = new Map<JobId, Map<PersonId, string>>();
    const ids = Object.keys(game.people) as PersonId[];
    for (const job of catalog().jobs) {
      const decision: Decision = { ...emptyDecision(game), assignments: {} };
      for (const id of ids) decision.assignments[id] = job.id;
      const blocked = new Map<PersonId, string>();
      for (const p of validateDecision(game, decision, cfg)) {
        if (p.code !== 'not_eligible') continue;
        blocked.set(p.path.slice('assignments.'.length) as PersonId, p.message);
      }
      out.set(job.id, blocked);
    }
    return out;
  },
);

const NEED_STOCK: Record<RationResource, Resource> = {
  food: 'food',
  water: 'water',
  warmth: 'wood',
  medicine: 'medicine',
};

/** Section 6 demand per ration resource next season, summed over everyone. */
export const demandOf = memo((game: GameState, cfg?: Config): Record<RationResource, number> => {
  const out: Record<RationResource, number> = { food: 0, water: 0, warmth: 0, medicine: 0 };
  for (const id of Object.keys(game.people).sort() as PersonId[])
    for (const resource of Object.keys(out) as RationResource[])
      out[resource] += evaluateTarget(game, { kind: 'demand', personId: id, resource }, cfg).value;
  return out;
});

/** Settlement-wide conditions (season, land, events, buildings, policies, norms) are left out. */
const SHARED_SOURCES = new Set(['season', 'land', 'event', 'building', 'policy', 'norm']);

/**
 * How productive this person would be in a job (Section 8: strength, skill, tools, happiness,
 * traits): the product of the person's own output multipliers, so jobs compare fairly whatever
 * the season.
 */
function multiplier(game: GameState, personId: PersonId, job: JobId, cfg?: Config): number {
  const ev = evaluateTarget(game, { kind: 'output', personId, jobId: job }, cfg);
  let product = 1;
  for (const f of ev.factors) {
    if (f.op !== 'mul') continue;
    const namespace = f.source.split(':')[0] ?? f.source;
    if (!SHARED_SOURCES.has(namespace)) product *= f.value;
  }
  return product;
}

function dipFor(
  game: GameState,
  person: PersonRow,
  job: JobId,
  cfg?: Config,
): DipPreview | null {
  if (job === 'rest' || job === person.job) return null;
  const p = game.people[person.id];
  if (p === undefined) return null;
  return {
    now: person.job === 'rest' ? null : multiplier(game, p.id, person.job, cfg),
    moved: multiplier(game, p.id, job, cfg),
    skillNow: person.job === 'rest' ? 0 : (p.skills[person.job] ?? 0),
    skillMoved: p.skills[job] ?? 0,
    losesPlot: person.job === 'farmer' && (game.plantedPlots[p.id] ?? 0) > 0,
  };
}

function seriesOf(projection: Projection | null, game: GameState, job: JobId): number[] {
  if (projection === null || projection.baseTurn !== game.turn || PRIMARY[job] === undefined)
    return [];
  return projection.seasons.map((s) => s.outputs[job] ?? 0);
}

export const jobsVM = memo(
  (
    game: GameState,
    draft: Decision | null,
    result: Projection | null,
    baseline: Projection | null,
    pending: boolean,
    selectedId: PersonId | null,
    cfg?: Config,
  ): JobsVM => {
    const rows = peopleRows(game, draft);
    const eligibility = eligibilityOf(game, cfg);
    const selected = selectedId === null ? null : (rows.find((r) => r.id === selectedId) ?? null);
    const last = game.history[game.history.length - 1];
    const columns = catalog().jobs.map((j): JobColumnVM => {
      const blocked = eligibility.get(j.id) ?? new Map<PersonId, string>();
      const output = PRIMARY[j.id] ?? null;
      const problem = selected === null ? null : (blocked.get(selected.id) ?? null);
      return {
        id: j.id,
        label: j.label,
        heavy: j.heavy,
        requiresBuilding: j.requiresBuilding,
        requiresLabel:
          j.requiresBuilding === null
            ? null
            : (catalog().buildings.find((b) => b.id === j.requiresBuilding)?.label ?? j.requiresBuilding),
        people: rows.filter((r) => r.job === j.id),
        count: rows.filter((r) => r.job === j.id).length,
        eligible: rows.length - blocked.size,
        vacancies: (last?.entries ?? []).flatMap((e) =>
          e.kind === 'job-vacant' && e.jobId === j.id
            ? [
                {
                  personId: e.personId,
                  name:
                    game.people[e.personId]?.name ?? game.formerPeople[e.personId]?.name ?? e.personId,
                  reason: e.reason,
                },
              ]
            : [],
        ),
        output,
        series: seriesOf(result, game, j.id),
        baseline: seriesOf(baseline, game, j.id),
        selectedEligible: selected === null ? null : problem === null,
        selectedProblem: problem,
        dip: selected === null || problem !== null ? null : dipFor(game, selected, j.id, cfg),
      };
    });
    const demand = demandOf(game, cfg);
    const next = result?.baseTurn === game.turn ? result.seasons[0] : undefined;
    const needs = (Object.keys(NEED_STOCK) as RationResource[]).map((resource) => {
      const stock = NEED_STOCK[resource];
      return {
        resource,
        needed: demand[resource],
        projected: next?.flows[stock].production ?? 0,
        onHand: game.stocks[stock].amount,
        stock,
      };
    });
    return {
      columns,
      needs,
      horizon:
        result?.baseTurn === game.turn
          ? result.seasons.map((s) => ({ season: s.season, year: s.year }))
          : [],
      pending,
      selected,
      selectedLosesPlot:
        selected !== null && selected.job === 'farmer' && (game.plantedPlots[selected.id] ?? 0) > 0,
    };
  },
);

// ---- Auto-fill diff (Section 22: proposes, never applies without a press).

export interface AutoFillRow {
  personId: PersonId;
  name: string;
  from: JobId;
  to: JobId;
  fromLabel: string;
  toLabel: string;
  skillFrom: number;
  skillTo: number;
}

export function autoFillVM(
  game: GameState,
  draft: Decision | null,
  proposal: Record<PersonId, JobId> | null,
): AutoFillRow[] {
  if (proposal === null) return [];
  const rows = peopleRows(game, draft);
  const label = (job: JobId): string => catalog().jobs.find((j) => j.id === job)?.label ?? job;
  return (Object.entries(proposal) as [PersonId, JobId][]).flatMap(([id, to]) => {
    const row = rows.find((r) => r.id === id);
    const p = game.people[id];
    if (row === undefined || p === undefined) return [];
    return [
      {
        personId: id,
        name: row.name,
        from: row.job,
        to,
        fromLabel: row.jobLabel,
        toLabel: label(to),
        skillFrom: row.job === 'rest' ? 0 : (p.skills[row.job] ?? 0),
        skillTo: to === 'rest' ? 0 : (p.skills[to] ?? 0),
      },
    ];
  });
}
